import { isPinnedBackgroundLayer, type ElementReorderCommand } from '@adflow/types';

export { isPinnedBackgroundLayer };
export type { ElementReorderCommand };

export const REORDER_COMMANDS = ['front', 'forward', 'backward', 'back'] as const;

export type StackableLayer = {
  id: string;
  type: string;
  name: string;
  position: { z: number };
};

export type ReorderCapabilities = {
  front: boolean;
  forward: boolean;
  backward: boolean;
  back: boolean;
};

export type ReorderPlan = {
  elementIds: string[];
  before: Record<string, number>;
  after: Record<string, number>;
};

type LayerRef<T extends StackableLayer> = {
  layer: T;
  index: number;
};

function isReorderCommand(value: string): value is ElementReorderCommand {
  return (REORDER_COMMANDS as readonly string[]).includes(value);
}

export function parseReorderCommand(value: unknown): ElementReorderCommand | null {
  return typeof value === 'string' && isReorderCommand(value) ? value : null;
}

export function compareVisualStack(
  a: { z: number; index: number },
  b: { z: number; index: number },
): number {
  if (b.z !== a.z) {
    return b.z - a.z;
  }
  return b.index - a.index;
}

export function sortLayersForList<T extends StackableLayer>(layers: readonly T[]): T[] {
  return layers
    .map((layer, index) => ({ layer, index }))
    .sort((a, b) =>
      compareVisualStack(
        { z: a.layer.position.z, index: a.index },
        { z: b.layer.position.z, index: b.index },
      ),
    )
    .map(({ layer }) => layer);
}

function overlayFrontToBack<T extends StackableLayer>(layers: readonly T[]): Array<LayerRef<T>> {
  return layers
    .map((layer, index) => ({ layer, index }))
    .filter(({ layer }) => !isPinnedBackgroundLayer(layer))
    .sort((a, b) =>
      compareVisualStack(
        { z: a.layer.position.z, index: a.index },
        { z: b.layer.position.z, index: b.index },
      ),
    );
}

export function overlayOrderIds(layers: readonly StackableLayer[]): string[] {
  return overlayFrontToBack(layers).map(({ layer }) => layer.id);
}

export function canReorderElement(
  layers: readonly StackableLayer[],
  elementId: string,
): ReorderCapabilities {
  const disabled = { front: false, forward: false, backward: false, back: false };
  const order = overlayOrderIds(layers);
  const index = order.indexOf(elementId);
  if (index < 0) {
    return disabled;
  }
  const canRaise = index > 0;
  const canLower = index < order.length - 1;
  return {
    front: canRaise,
    forward: canRaise,
    backward: canLower,
    back: canLower,
  };
}

export function computeReorder(
  layers: readonly StackableLayer[],
  elementId: string,
  command: ElementReorderCommand,
): ReorderPlan | null {
  const target = layers.find((layer) => layer.id === elementId);
  if (!target || isPinnedBackgroundLayer(target)) {
    return null;
  }

  const order = overlayFrontToBack(layers);
  const from = order.findIndex(({ layer }) => layer.id === elementId);
  if (from < 0) {
    return null;
  }

  let to = from;
  if (command === 'front') {
    to = 0;
  } else if (command === 'forward') {
    to = Math.max(0, from - 1);
  } else if (command === 'backward') {
    to = Math.min(order.length - 1, from + 1);
  } else {
    to = order.length - 1;
  }

  if (to === from) {
    return null;
  }

  const nextOrder = [...order];
  const [moved] = nextOrder.splice(from, 1);
  if (!moved) {
    return null;
  }
  nextOrder.splice(to, 0, moved);

  const pinnedZs = layers.filter(isPinnedBackgroundLayer).map((layer) => layer.position.z);
  const minReorderZ = pinnedZs.length > 0 ? Math.max(...pinnedZs) + 1 : 1;

  const before: Record<string, number> = {};
  const after: Record<string, number> = {};
  [...nextOrder].reverse().forEach((entry, index) => {
    const nextZ = minReorderZ + index;
    before[entry.layer.id] = entry.layer.position.z;
    after[entry.layer.id] = nextZ;
  });

  const changed = Object.keys(after).filter((id) => before[id] !== after[id]);
  if (changed.length === 0) {
    return null;
  }

  return { elementIds: Object.keys(after), before, after };
}
